import React from "react"
import { InvoiceTableData } from "./accounts-invoices"

// Shared between the "Print Cashier Report" modal (invoicesPrintModal.tsx —
// full letterhead print output) and the inline Cashier Report page
// (report/cashier-report/cashierReport.tsx — same tables, no logo/print
// chrome) so the two views can never drift apart on what counts as
// collected/discounted/fined.

export const getSectionName = (invoice: InvoiceTableData): string => {
  if (invoice.section) {
    if (typeof invoice.section === "string") return invoice.section
    if (typeof invoice.section === "object" && (invoice.section as any).name) {
      return (invoice.section as any).name
    }
  }

  const sectionDetails = (invoice.class as any)?.sectionDetails
  if (sectionDetails && Array.isArray(sectionDetails)) {
    const firstSection = sectionDetails[0]
    if (firstSection && firstSection.name) {
      return firstSection.name
    }
  }

  return "-"
}

export const formatMonth = (month: string): string => {
  if (!month) return month
  const parts = month.split("-")
  if (parts.length === 2) {
    const date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, 1)
    if (!isNaN(date.getTime())) {
      return date.toLocaleString("en-US", { month: "long", year: "numeric" })
    }
  }
  return month
}

export const formatDate = (dateString?: string) => {
  if (!dateString) return "N/A"
  try {
    const date = new Date(dateString)
    const day = date.getDate().toString().padStart(2, "0")
    const month = date.toLocaleString("en-US", { month: "2-digit" })
    const year = date.getFullYear()
    return `${day}/${month}/${year}`
  } catch {
    return dateString
  }
}

export type TuitionCategory = "Tuition" | "STEM"

export interface MonthBreakdownRow {
  month: string
  category: TuitionCategory
  invoiceIds: Set<string>
  subTotal: number
  discount: number
  fine: number
  paid: number
}

// Tuition Breakdown must only include items whose fee GROUP is "Tuition" —
// this is the one consistently-named field across every campus/system (the
// fee TYPE underneath it varies a lot in practice: "Tuition Fee", "Tution
// Fee-1" (typo, still real data), "Tuition Fee MJ/MS/PG/G1-3/G4-5" etc. — a
// keyword match on the type name alone misses these). Within that group,
// only the fee TYPE distinguishes STEAM/STEM (O-Level) from everything
// else, which is plain Tuition. Excludes Examination, Lab, Admission, etc.
// entirely, since those are different fee groups. Tuition and STEM get
// their own rows per month — O-Level campuses bill them as separate line
// items, so a month with both produces two rows; a month/system with only
// tuition produces just the one.
const classifyTuitionItem = (item: any): TuitionCategory | null => {
  const feeGroupName = item.feeGroup?.name?.toLowerCase?.() || ""
  if (feeGroupName !== "tuition") return null
  const feeTypeName = item.feeType?.name?.toLowerCase?.() || ""
  if (feeTypeName.includes("steam") || feeTypeName.includes("stem")) return "STEM"
  return "Tuition"
}

export interface FeeGroupBreakdownRow {
  invoiceIds: Set<string>
  subTotal: number
  discount: number
  fine: number
  paid: number
}

export interface CashierReportTotals {
  activeInvoices: InvoiceTableData[]
  totalCollected: number
  totalDiscount: number
  totalFine: number
  totalSubTotal: number
  tuitionBreakdown: MonthBreakdownRow[]
  feeGroupBreakdownMap: Record<string, FeeGroupBreakdownRow>
}

export const computeCashierReportTotals = (
  invoices: InvoiceTableData[]
): CashierReportTotals => {
  const activeInvoices = invoices.filter(
    (invoice) => invoice.status !== "cancelled"
  )

  const totalCollected = activeInvoices.reduce(
    (sum, invoice) => sum + (invoice.total || 0),
    0
  )
  const totalDiscount = activeInvoices.reduce(
    (sum, invoice) =>
      sum +
      ((invoice.discount?.manual || 0) + (invoice.discount?.profile || 0)),
    0
  )
  const totalFine = activeInvoices.reduce(
    (sum, invoice) => sum + (invoice.fine || 0),
    0
  )
  const totalSubTotal = activeInvoices.reduce(
    (sum, invoice) => sum + (invoice.subTotal || 0),
    0
  )

  const tuitionBreakdownMap = new Map<string, MonthBreakdownRow>()
  activeInvoices.forEach((invoice) => {
    const items = invoice.invoiceItems || []
    const invoiceSubTotal = invoice.subTotal || 0
    const invoiceFine = invoice.fine || 0
    items.forEach((item: any) => {
      const category = classifyTuitionItem(item)
      if (!category) return
      const month = item.month || "Unknown"
      const key = `${month}__${category}`
      const amount = item.amount || 0
      // Each line's own discount is always accurate — a shared invoice-level
      // discount doesn't mean every line was discounted equally (e.g. a
      // discount applied only to Admission must not also show up under
      // Tuition). Fine wasn't captured per line until recently, so older
      // items (before that field existed) fall back to an estimate; every
      // item since has the real value.
      const itemDiscount = item.discount || 0
      const ratio = invoiceSubTotal > 0 ? amount / invoiceSubTotal : 0
      const itemFine = item.fine !== undefined ? item.fine : ratio * invoiceFine
      if (!tuitionBreakdownMap.has(key)) {
        tuitionBreakdownMap.set(key, {
          month,
          category,
          invoiceIds: new Set(),
          subTotal: 0,
          discount: 0,
          fine: 0,
          paid: 0,
        })
      }
      const row = tuitionBreakdownMap.get(key)!
      row.invoiceIds.add(invoice._id)
      row.subTotal += amount
      row.discount += itemDiscount
      row.fine += itemFine
      row.paid += amount - itemDiscount + itemFine
    })
  })
  const tuitionBreakdown = Array.from(tuitionBreakdownMap.values()).sort((a, b) => {
    if (a.month !== b.month) return a.month.localeCompare(b.month)
    if (a.category === b.category) return 0
    return a.category === "Tuition" ? -1 : 1
  })

  const feeGroupBreakdownMap = activeInvoices.reduce(
    (acc, invoice) => {
      const items = invoice.invoiceItems || []
      const invoiceSubTotal = invoice.subTotal || 0
      const invoiceFine = invoice.fine || 0
      items.forEach((item: any) => {
        const name = item.feeGroup?.name || "Other"
        const amount = item.amount || 0
        const itemDiscount = item.discount || 0
        const ratio = invoiceSubTotal > 0 ? amount / invoiceSubTotal : 0
        const itemFine = item.fine !== undefined ? item.fine : ratio * invoiceFine
        if (!acc[name]) acc[name] = { invoiceIds: new Set(), subTotal: 0, discount: 0, fine: 0, paid: 0 }
        acc[name].invoiceIds.add(invoice._id)
        acc[name].subTotal += amount
        acc[name].discount += itemDiscount
        acc[name].fine += itemFine
        acc[name].paid += amount - itemDiscount + itemFine
      })
      return acc
    },
    {} as Record<string, FeeGroupBreakdownRow>
  )

  return {
    activeInvoices,
    totalCollected,
    totalDiscount,
    totalFine,
    totalSubTotal,
    tuitionBreakdown,
    feeGroupBreakdownMap,
  }
}

interface CashierReportTablesProps {
  totals: CashierReportTotals
}

// The 4 tables shared by the print output and the inline report page:
// invoice list, tuition breakdown, fee group breakdown, grand total.
export const CashierReportTables: React.FC<CashierReportTablesProps> = ({
  totals,
}) => {
  const {
    activeInvoices,
    totalCollected,
    totalDiscount,
    totalFine,
    totalSubTotal,
    tuitionBreakdown,
    feeGroupBreakdownMap,
  } = totals

  return (
    <>
      <div className="mb-4">
        <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <table
            className="table table-bordered"
            style={{ fontSize: "11px", minWidth: "1200px" }}
          >
            <thead style={{ backgroundColor: "#f2f2f2" }}>
              <tr>
                <th style={{ width: "30px" }}>S No.</th>
                <th>GR No.</th>
                <th>Student Name</th>
                <th>Receipt No.</th>
                <th>Class</th>
                <th style={{ textAlign: "center" }}>Section</th>
                <th>Month Paid</th>
                <th>Details</th>
                <th>Date</th>
                <th style={{ textAlign: "right" }}>Sub Total</th>
                <th style={{ textAlign: "right" }}>Discount</th>
                <th style={{ textAlign: "right" }}>Fine</th>
                <th style={{ textAlign: "right" }}>Paid Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {activeInvoices.map((invoice, index) => {
                const student = invoice.student
                const tuitionItems =
                  invoice.invoiceItems?.filter((item: any) =>
                    item.feeGroup?.name?.toLowerCase().includes("tuition")
                  ) || []
                const uniqueMonths = Array.from(
                  new Set(
                    tuitionItems.map((item: any) => item.month).filter(Boolean)
                  )
                )
                  .map(formatMonth)
                  .join(", ")
                const allFeeGroupNames = Array.from(
                  new Set(
                    invoice.invoiceItems
                      ?.map((item: any) => item.feeGroup?.name)
                      .filter(Boolean)
                  )
                ).join(", ")
                const discount =
                  (invoice.discount?.manual || 0) + (invoice.discount?.profile || 0)

                return (
                  <tr key={invoice._id || index}>
                    <td style={{ textAlign: "center" }}>{index + 1}</td>
                    <td>{invoice.student?.grNumber || "N/A"}</td>
                    <td>
                      {student?.user?.firstName} {student?.user?.lastName}
                    </td>
                    <td>{invoice.invoiceNumber || "N/A"}</td>
                    <td>{invoice.class?.name || "N/A"}</td>
                    <td style={{ textAlign: "center" }}>{getSectionName(invoice)}</td>
                    <td>{uniqueMonths}</td>
                    <td>{allFeeGroupNames || "-"}</td>
                    <td>{new Date(invoice.createdAt).toLocaleDateString()}</td>
                    <td style={{ textAlign: "right" }}>{invoice.subTotal?.toLocaleString()}</td>
                    <td style={{ textAlign: "right" }}>{discount.toLocaleString()}</td>
                    <td style={{ textAlign: "right" }}>{(invoice.fine || 0).toLocaleString()}</td>
                    <td style={{ textAlign: "right" }}>{invoice.total?.toLocaleString()}</td>
                    <td>{invoice.status}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-4">
        <h5 style={{ fontSize: "14px", marginBottom: "8px" }}>1. Tuition Breakdown</h5>
        <table className="table table-bordered summary-table" style={{ fontSize: "11px" }}>
          <thead style={{ backgroundColor: "#f2f2f2" }}>
            <tr>
              <th style={{ width: "40px" }}>S No.</th>
              <th>Month</th>
              <th>Fee Type</th>
              <th style={{ textAlign: "center" }}>Total Invoices</th>
              <th style={{ textAlign: "right" }}>Sub Total</th>
              <th style={{ textAlign: "right" }}>Discount</th>
              <th style={{ textAlign: "right" }}>Fine</th>
              <th style={{ textAlign: "right" }}>Paid Amount</th>
            </tr>
          </thead>
          <tbody>
            {tuitionBreakdown.map((data, index) => (
              <tr key={`${data.month}__${data.category}`}>
                <td style={{ textAlign: "center" }}>{index + 1}</td>
                <td>{formatMonth(data.month)}</td>
                <td>{data.category}</td>
                <td style={{ textAlign: "center" }}>{data.invoiceIds.size}</td>
                <td style={{ textAlign: "right" }}>{data.subTotal.toLocaleString()}</td>
                <td style={{ textAlign: "right" }}>{data.discount.toLocaleString()}</td>
                <td style={{ textAlign: "right" }}>{data.fine.toLocaleString()}</td>
                <td style={{ textAlign: "right" }}>{data.paid.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4">
        <h5 style={{ fontSize: "14px", marginBottom: "8px" }}>2. Fee Group Wise Breakdown</h5>
        <table className="table table-bordered summary-table" style={{ fontSize: "11px" }}>
          <thead style={{ backgroundColor: "#f2f2f2" }}>
            <tr>
              <th style={{ width: "40px" }}>S No.</th>
              <th>Fee Group</th>
              <th style={{ textAlign: "center" }}>Total Invoices</th>
              <th style={{ textAlign: "right" }}>Sub Total</th>
              <th style={{ textAlign: "right" }}>Discount</th>
              <th style={{ textAlign: "right" }}>Fine</th>
              <th style={{ textAlign: "right" }}>Paid Amount</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(feeGroupBreakdownMap).map(([name, data], index) => (
              <tr key={name}>
                <td style={{ textAlign: "center" }}>{index + 1}</td>
                <td>{name}</td>
                <td style={{ textAlign: "center" }}>{data.invoiceIds.size}</td>
                <td style={{ textAlign: "right" }}>{Math.round(data.subTotal).toLocaleString()}</td>
                <td style={{ textAlign: "right" }}>{Math.round(data.discount).toLocaleString()}</td>
                <td style={{ textAlign: "right" }}>{Math.round(data.fine).toLocaleString()}</td>
                <td style={{ textAlign: "right" }}>{Math.round(data.paid).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4">
        <h5 style={{ fontSize: "14px", marginBottom: "8px" }}>3. Grand Total</h5>
        <table className="table table-bordered summary-table" style={{ fontSize: "11px", maxWidth: "350px" }}>
          <tbody>
            <tr>
              <td style={{ fontWeight: "bold" }}>Total Invoices</td>
              <td style={{ textAlign: "right" }}>{activeInvoices.length}</td>
            </tr>
            <tr>
              <td style={{ fontWeight: "bold" }}>Sub Total</td>
              <td style={{ textAlign: "right" }}>Rs. {totalSubTotal.toLocaleString()}</td>
            </tr>
            <tr>
              <td style={{ fontWeight: "bold" }}>Discount</td>
              <td style={{ textAlign: "right" }}>Rs. {totalDiscount.toLocaleString()}</td>
            </tr>
            <tr>
              <td style={{ fontWeight: "bold" }}>Fine</td>
              <td style={{ textAlign: "right" }}>Rs. {totalFine.toLocaleString()}</td>
            </tr>
            <tr style={{ backgroundColor: "#f2f2f2" }}>
              <td style={{ fontWeight: "bold" }}>Net Paid Amount</td>
              <td style={{ textAlign: "right", fontWeight: "bold", color: "#28a745" }}>
                Rs. {totalCollected.toLocaleString()}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </>
  )
}
